import React, {useCallback, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Label} from "flowbite-react";
import {useJwt} from "../../helpers/common";
import {useOrganizationService, useTemplateService} from '../../services';
import {Drawer} from "../../components/Modals";
import {notify} from 'react-notify-toast';
import CustomButton from '../../components/common/CustomButton';
import FormInput from '../../components/common/FormInput';
import { SelectEntityDropdown } from '../../features/SelectEntity/SelectEntityDropdown';
import { StrategyMapListItem } from '../../services/strategyMap';

const AddUpdateTemplate = ({toggleCreateModal,setToggleCreateModal,payloadData,setPayloadData,onSuccess}:any) => {
  const JWTdata: any = useJwt()
  const org_id = JWTdata.org_id;
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { saveTemplate,updateTemplate } =  useTemplateService();

  useEffect(() => {
    if (toggleCreateModal && !payloadData.template_id) {
      setPayloadData({});
    }
  }, [toggleCreateModal]);

  const handleCloseCreateModal = () => {
    setToggleCreateModal(false)
    setPayloadData({})
  }

  const handleOnChange = (e:any)=> {
    const {name, value, type, checked} = e.target;
    const data = {
      [name]: type === 'checkbox' ? checked : value
    }
    setPayloadData((pv:any)=>({...pv,...data}))
  }

  const handleSubmitClick = async (e:any)=> {
    const paylaod = {
      ...payloadData,
      include_delay: payloadData.include_delay ?? false
    }
    const {project_name = "",project_description = ""} = paylaod
    if(!project_name){
      notify.show("Template name can not be empty.", "error", 3000);
      return
    }
    if(!project_description){
      notify.show("Template description can not be empty.", "error", 3000);
      return
    }

    setIsSubmitting(true);
    try {
      if(paylaod.template_id){
        await updateTemplate(paylaod)
        window.location.href = `/templates/${payloadData.template_id}?history=%5B%7B"name"%3A"Work+Items"%2C"url"%3A"%2Fworkitems"%7D%5D`;

      } else {

        const res = await saveTemplate(paylaod)
        if(res){
          setToggleCreateModal(false)
          if(onSuccess) {
            onSuccess();
          }
          if(res?.template_id){
            window.location.href = `/templates/${res.template_id}?history=%5B%7B"name"%3A"Work+Items"%2C"url"%3A"%2Fworkitems"%7D%5D`;
          }else{
            window.location.href = `/templates`;
          }
        }
      }
    } catch (error) {
      console.error('Error updating template:', error);
      notify.show("Failed to save template. Please try again.", "error", 3000);
    } finally {
      setIsSubmitting(false);
    }
  }

  const handleEntitiesChange = (entities: StrategyMapListItem[]) => {
    const selectedProjectId = entities.length > 0 ? entities[0].id : "";
    const selectedProjectName = entities.length > 0 ? entities[0].name : "";

    // If template name is empty, we can auto-fill it with the selected project's name
    handleOnChange({
      target: {
        name: "project_id",
        value: selectedProjectId,
      },
    });
    // Save the entire selected object to payload
    handleOnChange({
      target: {
        name: "selectedProjectData",
        value: entities.length > 0 ? entities.map(e => ({id: e.id, name: e.name, type: e.type, entity_type: e.entity_type}))[0] : null,
      },
    });

    if (selectedProjectName) {
      handleOnChange({
        target: {
          name: "project_name",
          value: selectedProjectName,
        },
      });
    }else{
      handleOnChange({
        target: {
          name: "project_name",
          value: "",
        },
      });
    }
  };

  const selectedEntities = payloadData.project_id
    ? [{ 
        id: payloadData.project_id, 
        name: "Selected Project", 
        type: "project",
        entity_type: "project"
      }]
    : [];

  return (
    <>
      {toggleCreateModal  && (
        <Drawer
          isOpen={toggleCreateModal}
          onClose={handleCloseCreateModal}
          position="right"
        >
          <div className="h-screen w-full flex flex-col bg-light-surface dark:bg-tertiary-dark overflow-hidden">
            <div className="flex-shrink-0 flex flex-row border-b z-50 rounded-b-2xl border-gray-400 dark:border-hover-dark bg-light-surfacePrimary dark:bg-tertiary-dark px-6 py-4 text-light-text-primary dark:text-white shadow-lg">
              <div className="flex w-full items-center min-h-[60px]">
                <div className="flex flex-col flex-1">
                  <div className="text-light-text-primary dark:text-white text-lg font-semibold">
                    {payloadData.template_id ? "Update Template" : "Create Template"}
                  </div>
                  {payloadData.template_id && (
                    <div className="text-light-text-secondary dark:text-white/70 text-xs mt-0.5">
                      {payloadData.created_at ? `Created on: ${new Date(payloadData.created_at).toLocaleDateString()}` : null}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="text-light-text-secondary hover:text-light-text-primary dark:text-white/70 dark:hover:text-white hover:bg-light-interactive-hover dark:hover:bg-white/10 p-2 rounded-lg transition-colors duration-200"
                  onClick={handleCloseCreateModal}>
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"></path>
                  </svg>
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto bg-light-surface dark:bg-tertiary-dark">
              <div className="px-12 py-8 space-y-6">
                <div className="w-full">
                  <div
                    className="dark:text-gray-400 text-gray-600 text-base font-normal leading-normal mb-6"
                  >
                    {`Cadence will use your existing Normal Epic / Feature tasks' name, duration, and description to start a template that you can reuse to start future work items. You can find your created templates under Templates.`}
                  </div>
                  {!payloadData.template_id && (
                    <div className="space-y-2 mb-6">
                      <Label htmlFor="project_select" className="text-base font-medium text-light-text-primary dark:text-white">
                        Existing Normal Epic / Feature
                        <span className="text-light-status-error-text dark:text-red-400 ml-1">*</span>
                      </Label>
                      <SelectEntityDropdown
                        selectedEntities={payloadData.selectedProjectData ? [payloadData.selectedProjectData] : []}
                        onChange={handleEntitiesChange}
                        placeholder="Choose Normal Epic / Feature"
                        searchType="project"
                        selectOne={true}
                        defaultSelectedIds={payloadData.project_id ? { project_ids: [payloadData.project_id] } : undefined}
                      />
                    </div>
                  )}

                  <div className="space-y-2 mb-6">
                    <Label htmlFor="projectname" className="text-base font-medium text-light-text-primary dark:text-white">
                      Template name
                      <span className="text-light-status-error-text dark:text-red-400 ml-1">*</span>
                    </Label>
                  <FormInput
                    id="projectname"
                    name="project_name"
                    label=""
                    type="text"
                    value={payloadData?.project_name}
                    onChange={handleOnChange}
                    placeholder="Template Name"
                    maxLength={100}
                    counter
                    required
                    showAsterisk={false}
                  />
                  </div>

                  <div className="space-y-2 mb-6">
                    <Label htmlFor="description" className="text-base font-medium text-light-text-primary dark:text-white">
                      Description
                      <span className="text-light-status-error-text dark:text-red-400 ml-1">*</span>
                    </Label>
                  <FormInput
                    id="description"
                    name="project_description"
                      label=""
                    type="textarea"
                    value={payloadData?.project_description}
                    onChange={handleOnChange}
                    placeholder="Description"
                    maxLength={2000}
                    counter
                    required
                    showAsterisk={false}
                  />
                  </div>

                  {!payloadData.template_id && (
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="include_delay"
                          name="include_delay"
                          checked={payloadData.include_delay ?? false}
                          onChange={handleOnChange}
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                        />
                        <Label htmlFor="include_delay" className="ml-2 text-base font-medium text-light-text-primary dark:text-white">
                          Include Delays
                        </Label>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex-shrink-0 px-8 py-4 items-center flex gap-6 border-t border-gray-500 rounded-t-md dark:border-hover-dark bg-light-surfacePrimary dark:bg-tertiary-dark shadow-lg">
              <CustomButton
                onClick={handleCloseCreateModal}
                variant="cancel"
                className="px-6 py-3 rounded-lg font-medium transition-all duration-200 hover:scale-105"
              >
                Cancel
              </CustomButton>
              <CustomButton
                onClick={handleSubmitClick}
                disabled={isSubmitting}
                className={`ml-auto px-6 py-3 rounded-lg font-medium transition-all duration-200 shadow-md hover:shadow-lg ${
                  isSubmitting 
                    ? 'opacity-75 cursor-not-allowed' 
                    : 'hover:scale-105'
                }`}
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {payloadData.template_id ? "Updating..." : "Creating..."}
                  </span>
                ) : (
                  payloadData.template_id ? "Update" : "Create"
                )}
              </CustomButton>
            </div>
          </div>
        </Drawer>
      )}
    </>
  )
}

export default AddUpdateTemplate;